// utils/getDobByName.js
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const norm = (s = "") => s.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Extract a clean "name" if input is like "William Smith - SIPP"
 */
const extractNameOnly = (input = "") => {
  const [nameOnly] = String(input).split(" - ");
  return nameOnly || "";
};

/**
 * Returns DOB string (as stored) for the given display name.
 * - Looks in state.details[{ name, dob }]
 * - Case/whitespace-insensitive match on `name`
 * - If not found, tries a startsWith match (for short inputs like "William")
 *
 * @param {object} state - Your global CFM state with `details` array
 * @param {string} displayName - e.g., "William Smith" or "William Smith - SIPP"
 * @returns {string} dob in "DD/MM/YYYY" (or "" if not found)
 */
export function getDobByName(state, displayName) {
  const details = Array.isArray(state?.details) ? state.details : [];
  const target = norm(extractNameOnly(displayName));

  if (!target) return "";

  // Exact normalized match
  const exact = details.find((d) => norm(d?.name) === target);
  if (exact?.dob) return exact.dob;

  // Fallback: startsWith (handles inputs like "William")
  const partial = details.find((d) => norm(d?.name).startsWith(target));
  return partial?.dob || "";
}

/**
 * Optional helper: compute integer age on a given month (MM/YYYY).
 * Accepts DOB in "DD/MM/YYYY" (preferred) or ISO "YYYY-MM-DD".
 *
 * @param {string} dob - "DD/MM/YYYY" or ISO
 * @param {string} mmYYYY - "MM/YYYY"
 * @returns {number} age in years (NaN if invalid)
 */
export function getAgeOn(dob, mmYYYY) {
  if (!dob || !mmYYYY) return NaN;

  const ref = dayjs(`01/${mmYYYY}`, "DD/MM/YYYY", true);
  if (!ref.isValid()) return NaN;

  const dobParsed = dayjs(dob, "DD/MM/YYYY", true).isValid()
    ? dayjs(dob, "DD/MM/YYYY", true)
    : dayjs(dob, "YYYY-MM-DD", true).isValid()
    ? dayjs(dob, "YYYY-MM-DD", true)
    : dayjs(dob); // last resort

  if (!dobParsed.isValid()) return NaN;

  return ref.diff(dobParsed, "year");
}
